// Navigation structure for desktop header menus and the mobile sheet.
// Internal link sections (play-now, discover, more) are derived from the
// route manifest; external links (connect) and actions (account) stay local
// because they don't correspond to app routes.

import { BRAND_LINKS, buildSupportMailto } from '@airhead-games/shared/configs';
import type { NavSectionKey } from './manifest';
import { getNavEntriesForSection } from './getFrontendRoutes';

export type NavLinkItem = {
  kind: 'link';
  label: string;
  path: string;
  external?: boolean;
};

export type NavActionItem = {
  kind: 'action';
  action: 'logout';
  label: string;
  destructive?: boolean;
};

export type NavItem = NavLinkItem | NavActionItem;

export type NavSection = {
  key: NavSectionKey | 'connect' | 'account';
  title: string;
  items: NavItem[];
};

const buildSection = (key: NavSectionKey, title: string): NavSection => ({
  key,
  title,
  items: getNavEntriesForSection(key).map<NavLinkItem>((entry) => ({
    kind: 'link',
    label: entry.nav!.label,
    path: entry.path,
  })),
});

const connectItems: NavLinkItem[] = [
  {
    kind: 'link',
    path: BRAND_LINKS.discord,
    label: "Poppy's Pals Discord",
    external: true,
  },
  {
    kind: 'link',
    path: buildSupportMailto('Support Request'),
    label: 'Email Support',
    external: true,
  },
  {
    kind: 'link',
    path: BRAND_LINKS.twitchDirectory,
    label: 'Twitch',
    external: true,
  },
  {
    kind: 'link',
    path: BRAND_LINKS.youtube,
    label: 'YouTube',
    external: true,
  },
  {
    kind: 'link',
    path: BRAND_LINKS.x,
    label: 'X',
    external: true,
  },
  {
    kind: 'link',
    path: BRAND_LINKS.instagram,
    label: 'Instagram',
    external: true,
  },
  {
    kind: 'link',
    path: BRAND_LINKS.tiktok,
    label: 'TikTok',
    external: true,
  },
];

const accountItems: NavActionItem[] = [
  {
    kind: 'action',
    action: 'logout',
    label: 'Log Out',
    destructive: true,
  },
];

export const getDesktopNavSections = (): NavSection[] => [
  buildSection('play-now', 'Play Now'),
  buildSection('discover', 'Discover'),
  buildSection('more', 'More'),
  { key: 'connect', title: 'Connect', items: connectItems },
];

export const getMobileNavSections = (isLoggedIn: boolean): NavSection[] => [
  ...getDesktopNavSections(),
  ...(isLoggedIn
    ? [
        {
          key: 'account' as const,
          title: 'Account',
          items: accountItems,
        },
      ]
    : []),
];
